/* eslint-disable @typescript-eslint/camelcase */
import cn from "classnames"
import React, { useRef } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "~/components/Button/Button"
import { DatePicker } from "~/components/DatePicker/DatePicker"
import { useFilterContext } from "~/components/FilterWrapper/FilterWrapper"
import { Popover } from "~/components/Popover/Popover"
import { ShadowField } from "~/components/ShadowField/ShadowField"
import { formatDate } from "~/helpers/formatDate"
import useOutsideClick from "~/helpers/useOutsideClick"
import { useSetState } from "~/helpers/useSetState"

import styles from "./index.module.css"

type State = {
  date_start?: Date
  date_end?: Date
  isOpen: boolean
}

const parseDate = (date: Date): Date => {
  return (new Date(date).toISOString().split("T")[0] as unknown) as Date
}
export const CalendarFilter: React.FC = () => {
  const { updateFilterState, filterState } = useFilterContext()
  const { t } = useTranslation()
  const [{ date_start, date_end, isOpen }, setState] = useSetState<State>({
    date_start: filterState.date_start,
    date_end: filterState.date_end,
    isOpen: false,
  })

  const wrapper = useRef(null)

  const closeHandler = (): void => setState({ isOpen: false })
  const toggleHandler = (): void => setState({ isOpen: isOpen ? false : true })

  const checkFirstDateSelected = (): void => {
    if (date_start && !date_end) {
      setState({ date_start, date_end: date_start })

      updateFilterState({
        date_start: parseDate(date_start),
        date_end: parseDate(date_start),
      })
    }
  }

  useOutsideClick(wrapper, () => {
    checkFirstDateSelected()
    closeHandler()
  })

  const changeHandler = ({
    date_start,
    date_end,
  }: {
    date_start?: Date
    date_end?: Date
  }): void => {
    setState({ date_start, date_end })

    if (date_start && date_end) {
      updateFilterState({
        date_start: parseDate(date_start),
        date_end: parseDate(date_end),
      })

      closeHandler()
    }
  }

  const clearDate = (): void => {
    updateFilterState({ date_start: undefined, date_end: undefined })
    setState({ date_start: undefined, date_end: undefined })
    closeHandler()
  }

  const showClearButton = !!date_start && !!date_end

  return (
    <div ref={wrapper} className={styles.Wrapper}>
      <ShadowField
        icon={!showClearButton ? "calendar" : undefined}
        iconComponent={
          showClearButton
            ? () => <div className={styles.ClearButton} onClick={clearDate} />
            : undefined
        }
        onClick={toggleHandler}
      >
        <div className={styles.Text}>
          <Text end={date_end} start={date_start} />
        </div>
      </ShadowField>

      <Popover className={styles.CalendarWrapper} isOpen={isOpen}>
        <DatePicker selectedDays={{ date_start, date_end }} onChange={changeHandler} />
        {/* <Button
          className={styles.ClearButton}
          data-state={cn({ clear: date_start && date_end })}
          view="rounded"
          onClick={clearDate}
        >
          {t("date.picker.clear")}
        </Button> */}
      </Popover>
    </div>
  )
}

type TextProps = {
  start?: Date
  end?: Date
}

const Text: React.FC<TextProps> = ({ start, end }) => {
  const { t } = useTranslation()
  if (!start) {
    return <>{t("calendar.Filter.Title")}</>
  }

  // TODO: нельзя использовать formatDateNew
  // написать свой formatDate, как здесь написан свой parseDate
  const dateStart = formatDate(start, { year: "2-digit", month: "numeric" })

  if (!end) {
    return (
      <>
        {dateStart}&nbsp;-&nbsp;<span>{t("date.picker.dateFormat")}</span>
      </>
    )
  }

  // TODO: нельзя использовать formatDateNew
  // написать свой formatDate, как здесь написан свой parseDate
  const dateEnd = formatDate(end, { year: "2-digit", month: "numeric" })

  if (dateStart === dateEnd) {
    return <>{dateStart}</>
  }

  return <>{`${dateStart} - ${dateEnd}`}</>
}
